import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Eye, EyeOff, Gamepad2, LockKeyhole, Mail, UserRound } from 'lucide-react';
import { authService } from './authService';
import { Button } from '@/components/ui/button';

const benefits = [
  'Все покупки в одном месте',
  'Персональные предложения',
  'Избранное всегда под рукой',
  'Быстрое оформление заказов',
];

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await authService.register(email, username, password);
      navigate('/');
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Ошибка регистрации';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#090a0d] text-white">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-32 -top-32 h-[32rem] w-[32rem] rounded-full bg-red-600/20 blur-[120px]" />
        <div className="absolute -bottom-48 right-0 h-[36rem] w-[36rem] rounded-full bg-red-950/40 blur-[140px]" />
        <div className="absolute inset-0 opacity-[0.035] [background-image:linear-gradient(rgba(255,255,255,.8)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.8)_1px,transparent_1px)] [background-size:48px_48px]" />
      </div>

      <div className="relative mx-auto grid min-h-screen w-full max-w-[90rem] lg:grid-cols-[1.05fr_.95fr]">
        <section className="hidden flex-col justify-between px-10 py-10 lg:flex xl:px-20 xl:py-14">
          <Link to="/" className="flex w-fit items-center gap-3 text-xl font-bold tracking-tight">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-red-500 to-red-700 shadow-lg shadow-red-950/50">
              <Gamepad2 className="h-6 w-6" />
            </span>
            FreeApp
          </Link>

          <div className="max-w-xl py-10">
            <span className="mb-6 inline-flex items-center gap-2 rounded-full border border-red-500/20 bg-red-500/10 px-4 py-2 text-sm font-medium text-red-300">
              <span className="h-2 w-2 rounded-full bg-red-500 shadow-[0_0_12px_rgba(239,68,68,.9)]" />
              Присоединяйтесь к FreeApp
            </span>
            <h1 className="text-5xl font-black leading-[1.05] tracking-[-0.04em] xl:text-7xl">
              Игра начинается
              <br />
              <span className="bg-gradient-to-r from-red-400 via-red-500 to-orange-400 bg-clip-text text-transparent">прямо сейчас.</span>
            </h1>
            <p className="mt-7 max-w-lg text-lg leading-relaxed text-white/50">
              Создайте аккаунт, чтобы покупать быстрее, сохранять любимые товары и получать лучшие предложения.
            </p>

            <div className="mt-10 grid max-w-lg grid-cols-2 gap-4">
              {benefits.map((item) => (
                <div key={item} className="flex items-center gap-3 text-sm text-white/70">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-500/15 text-red-400">
                    <Check className="h-3.5 w-3.5" strokeWidth={3} />
                  </span>
                  {item}
                </div>
              ))}
            </div>
          </div>

          <p className="text-sm text-white/25">© {new Date().getFullYear()} FreeApp</p>
        </section>

        <section className="flex min-h-screen items-center justify-center px-4 py-8 sm:px-8 lg:bg-white/[0.025] lg:backdrop-blur-sm">
          <div className="w-full max-w-[29rem]">
            <div className="mb-8 flex items-center justify-between lg:hidden">
              <Link to="/" className="flex items-center gap-2.5 text-lg font-bold">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-red-500 to-red-700">
                  <Gamepad2 className="h-5 w-5" />
                </span>
                FreeApp
              </Link>
              <Link to="/" aria-label="Вернуться на главную" className="rounded-full border border-white/10 p-2.5 text-white/60 transition hover:bg-white/5 hover:text-white">
                <ArrowLeft className="h-5 w-5" />
              </Link>
            </div>

            <div className="rounded-[2rem] border border-white/10 bg-white/[0.055] p-6 shadow-2xl shadow-black/30 backdrop-blur-xl sm:p-10">
              <div className="mb-7">
                <p className="mb-2 text-sm font-semibold uppercase tracking-[0.18em] text-red-400">Новый аккаунт</p>
                <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Регистрация</h2>
                <p className="mt-3 text-sm leading-relaxed text-white/45">Всего три поля — и вы в игре.</p>
              </div>

              {error && (
                <div role="alert" className="mb-5 rounded-xl border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="register-email" className="mb-2 block text-sm font-medium text-white/75">Email</label>
                  <div className="group relative">
                    <Mail className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-white/25 transition group-focus-within:text-red-400" />
                    <input id="register-email" type="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} required className="h-14 w-full rounded-2xl border border-white/10 bg-black/25 pl-12 pr-4 text-white outline-none transition placeholder:text-white/20 hover:border-white/20 focus:border-red-500/70 focus:ring-4 focus:ring-red-500/10" placeholder="you@example.com" />
                  </div>
                </div>

                <div>
                  <label htmlFor="username" className="mb-2 block text-sm font-medium text-white/75">Имя пользователя</label>
                  <div className="group relative">
                    <UserRound className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-white/25 transition group-focus-within:text-red-400" />
                    <input id="username" type="text" autoComplete="username" value={username} onChange={e => setUsername(e.target.value)} required className="h-14 w-full rounded-2xl border border-white/10 bg-black/25 pl-12 pr-4 text-white outline-none transition placeholder:text-white/20 hover:border-white/20 focus:border-red-500/70 focus:ring-4 focus:ring-red-500/10" placeholder="Как к вам обращаться?" />
                  </div>
                </div>

                <div>
                  <label htmlFor="register-password" className="mb-2 block text-sm font-medium text-white/75">Пароль</label>
                  <div className="group relative">
                    <LockKeyhole className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-white/25 transition group-focus-within:text-red-400" />
                    <input id="register-password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" value={password} onChange={e => setPassword(e.target.value)} required minLength={6} className="h-14 w-full rounded-2xl border border-white/10 bg-black/25 pl-12 pr-12 text-white outline-none transition placeholder:text-white/20 hover:border-white/20 focus:border-red-500/70 focus:ring-4 focus:ring-red-500/10" placeholder="Минимум 6 символов" />
                    <button type="button" onClick={() => setShowPassword(value => !value)} aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 transition hover:text-white/70">
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <Button type="submit" disabled={loading} className="group h-14 w-full rounded-2xl bg-gradient-to-r from-red-600 to-red-500 text-base font-semibold text-white shadow-lg shadow-red-950/40 transition hover:from-red-500 hover:to-red-500 hover:shadow-red-900/50 disabled:opacity-60">
                  {loading ? (
                    <span className="flex items-center gap-2"><span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />Создаём аккаунт...</span>
                  ) : (
                    <span className="flex items-center gap-2">Создать аккаунт <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" /></span>
                  )}
                </Button>
              </form>

              <div className="mt-7 flex items-center gap-3 text-xs text-white/25"><span className="h-px flex-1 bg-white/10" />Уже зарегистрированы?<span className="h-px flex-1 bg-white/10" /></div>
              <Link to="/login" className="mt-5 flex h-14 w-full items-center justify-center rounded-2xl border border-white/10 px-4 text-sm font-semibold text-white/80 transition hover:border-white/20 hover:bg-white/5 hover:text-white">
                Войти в аккаунт
              </Link>
            </div>

            <p className="mt-5 text-center text-xs leading-relaxed text-white/25">Создавая аккаунт, вы принимаете условия использования<br className="hidden sm:block" /> и политику конфиденциальности.</p>
          </div>
        </section>
      </div>
    </main>
  );
};

export default RegisterPage;
